import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { authApi } from '@/features/auth/api'
import { ApiError } from '@/lib/apiClient'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/Feedback'
import { PasswordField } from '@/components/ui/PasswordField'

const MIN_PASSWORD = 12
const MAX_PASSWORD = 72

function resetToken(hash: string) {
  return new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).get('token')?.trim() ?? ''
}

/**
 * Both halves of a forgotten password: without a token it asks for the account's email,
 * with the token the email's link carries it sets the new password.
 */
export default function ResetPasswordPage() {
  const location = useLocation()
  const [token, setToken] = useState(() => resetToken(location.hash))

  // The token leaves the address bar once read, so it is not kept in the history.
  useEffect(() => {
    if (location.hash) window.history.replaceState(null, '', location.pathname + location.search)
  }, [location.hash, location.pathname, location.search])

  return token ? <NewPassword token={token} onRestart={() => setToken('')} /> : <RequestLink />
}

function RequestLink() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState('')
  const [error, setError] = useState('')
  const formRef = useRef<HTMLFormElement>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true); setError('')
    if (!email.trim()) { formRef.current?.querySelector<HTMLInputElement>('input')?.focus(); return }
    setBusy(true)
    try {
      const response = await authApi.requestPasswordReset(email.trim())
      setSent(response.message)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos enviar el enlace. Inténtalo de nuevo.')
    } finally { setBusy(false) }
  }

  return <div className="auth-page">
    <header className="flow-heading">
      <p className="eyebrow">Cuenta BsPOS</p>
      <h1>Restablece tu contraseña</h1>
      <p>Escribe el correo con el que te registraste y te enviaremos un enlace para elegir una contraseña nueva. Si tienes varios correos y no recuerdas cuál usaste, te diremos si alguno no tiene cuenta.</p>
    </header>
    {sent ? <Notice tone="success">{sent}</Notice> : <form ref={formRef} noValidate onSubmit={submit} className="form-panel">
      <div className="form-field">
        <label htmlFor="reset-email">Correo electrónico</label>
        <input id="reset-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)}
          aria-invalid={submitted && !email.trim()} aria-describedby={submitted && !email.trim() ? 'reset-email-error' : undefined} />
        {submitted && !email.trim() && <p id="reset-email-error" className="field-error" role="alert">Escribe tu correo electrónico.</p>}
      </div>
      {error && <Notice>{error}</Notice>}
      <Button type="submit" busy={busy}>Enviar enlace</Button>
    </form>}
    <p className="flow-footnote"><Link to="/login">Volver a ingresar</Link></p>
  </div>
}

function NewPassword({ token, onRestart }: { token: string; onRestart: () => void }) {
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState('')
  const [error, setError] = useState('')
  const formRef = useRef<HTMLFormElement>(null)

  const passwordError = password.length < MIN_PASSWORD || password.length > MAX_PASSWORD ? `Usa entre ${MIN_PASSWORD} y ${MAX_PASSWORD} caracteres.` : undefined
  const confirmationError = confirmation !== password ? 'Las dos contraseñas no coinciden.' : undefined

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true); setError('')
    if (passwordError || confirmationError) {
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
      return
    }
    setBusy(true)
    try {
      const response = await authApi.resetPassword(token, password)
      setDone(response.message)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'No pudimos cambiar la contraseña. Inténtalo de nuevo.')
    } finally { setBusy(false) }
  }

  if (done) return <div className="auth-page">
    <header className="flow-heading"><p className="eyebrow">Cuenta BsPOS</p><h1>Contraseña actualizada</h1></header>
    <Notice tone="success">{done}</Notice>
    <p className="flow-footnote"><Link to="/login">Ingresar</Link></p>
  </div>

  return <div className="auth-page">
    <header className="flow-heading">
      <p className="eyebrow">Cuenta BsPOS</p>
      <h1>Elige una contraseña nueva</h1>
      <p>Al guardarla, inicia sesión de nuevo con ella.</p>
    </header>
    <form ref={formRef} noValidate onSubmit={submit} className="form-panel">
      <PasswordField label="Contraseña nueva" autoComplete="new-password" value={password} onChange={setPassword} minLength={MIN_PASSWORD}
        hint={`Entre ${MIN_PASSWORD} y ${MAX_PASSWORD} caracteres.`} error={submitted ? passwordError : undefined} />
      <PasswordField label="Confirma la contraseña" autoComplete="new-password" value={confirmation} onChange={setConfirmation} minLength={MIN_PASSWORD}
        error={submitted && !passwordError ? confirmationError : undefined} />
      {error && <Notice>{error} <button type="button" className="text-action" onClick={onRestart}>Pedir otro enlace</button></Notice>}
      <Button type="submit" busy={busy}>Guardar contraseña</Button>
    </form>
  </div>
}
